import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  FiArrowLeft,
  FiEdit,
  FiTrash2,
  FiBook,
  FiLayers,
  FiVideo,
  FiClock,
  FiCheck,
  FiUnlock,
  FiLock,
  FiActivity,
  FiTrendingUp,
  FiUsers,
} from 'react-icons/fi'
import adminApi from '../../api/adminApi'
import StatusBadge from '../../components/StatusBadge/StatusBadge'
import Modal from '../../components/Modal/Modal'
import Button from '../../components/Button/Button'
import FormInput from '../../components/FormInput/FormInput'
import Select from '../../components/Select/Select'
import Table from '../../components/Table/Table'
import './ModuleDetail.css'

const ModuleDetail = () => {
  const { moduleId } = useParams()
  const navigate = useNavigate()

  const [module, setModule] = useState(null)
  const [batch, setBatch] = useState(null)
  const [course, setCourse] = useState(null)
  const [lectures, setLectures] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [openModule, setOpenModule] = useState(null)
  const [batchModules, setBatchModules] = useState([])

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    order: '',
    duration: '',
    isOpen: false,
  })

  useEffect(() => {
    loadModule()
  }, [moduleId])

  const loadModule = async () => {
    setLoading(true)
    setError(null)
    try {
      // Fetch module details
      const moduleResponse = await adminApi.get(`/modules/${moduleId}`)
      const moduleData = moduleResponse.data
      setModule(moduleData)

      // Extract batch and course IDs
      const batchId = moduleData.batch?._id || moduleData.batch
      const courseId = moduleData.course?._id || moduleData.course

      // Fetch batch details
      if (batchId) {
        try {
          const batchResponse = await adminApi.get(`/batches/${batchId}`)
          setBatch(batchResponse.data)
          // Extract course from batch if not already set
          if (batchResponse.data.course) {
            setCourse(typeof batchResponse.data.course === 'object' ? batchResponse.data.course : null)
          }
        } catch (err) {
          console.error('Failed to load batch:', err)
        }
      }

      // Fetch course details if not from batch
      if (courseId && !course) {
        try {
          const courseResponse = await adminApi.get(`/courses/${courseId}`)
          setCourse(courseResponse.data)
        } catch (err) {
          console.error('Failed to load course:', err)
        }
      }

      // Fetch lectures for this module
      if (batchId) {
        try {
          const lecturesResponse = await adminApi.get(`/batches/${batchId}/lectures`, {
            params: { limit: 1000 },
          })
          const allLectures = lecturesResponse.data?.lectures || lecturesResponse.data?.data || []
          const moduleLectures = allLectures.filter(
            (l) => l.module?._id === moduleId || l.module === moduleId
          )
          setLectures(moduleLectures)
        } catch (err) {
          console.error('Failed to load lectures:', err)
        }
      }

      // Fetch currently open module for this batch
      if (batchId) {
        try {
          const openModuleResponse = await adminApi.get(`/batches/${batchId}/open-module`)
          setOpenModule(openModuleResponse.data)
        } catch (err) {
          // No open module or endpoint doesn't exist
          setOpenModule(null)
        }

        // Fetch all modules in this batch for reordering
        try {
          const modulesResponse = await adminApi.get(`/batches/${batchId}/modules`)
          const allModules = modulesResponse.data?.modules || modulesResponse.data?.data || []
          setBatchModules(allModules.sort((a, b) => (a.order || 0) - (b.order || 0)))
        } catch (err) {
          console.error('Failed to load batch modules:', err)
        }
      }

      // Set form data
      setFormData({
        title: moduleData.title || '',
        description: moduleData.description || '',
        order: moduleData.order || '',
        duration: moduleData.duration || '',
        isOpen: moduleData.isOpen || false,
      })
    } catch (err) {
      console.error('Failed to load module:', err)
      setError(err.response?.data?.message || 'Failed to load module')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    setEditLoading(true)
    try {
      await adminApi.patch(`/modules/${moduleId}`, formData)
      setShowEditModal(false)
      loadModule()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update module')
    } finally {
      setEditLoading(false)
    }
  }

  const handleDelete = async () => {
    setDeleteLoading(true)
    try {
      await adminApi.delete(`/modules/${moduleId}`)
      navigate('/modules')
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete module')
      setDeleteLoading(false)
    }
  }

  const handleToggleModule = async (isOpen) => {
    try {
      const endpoint = isOpen
        ? `/modules/${moduleId}/close`
        : `/modules/${moduleId}/open`
      await adminApi.patch(endpoint)
      loadModule()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to toggle module')
    }
  }

  const handleReorderModule = async (moduleIdToReorder, newOrder) => {
    try {
      await adminApi.patch(`/modules/${moduleIdToReorder}`, { order: newOrder })
      loadModule() // Reload to get updated order
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reorder module')
    }
  }

  if (loading) {
    return <div className="detail-loading">Loading module details...</div>
  }

  if (error && !module) {
    return (
      <div className="detail-error">
        <p>{error}</p>
        <Button onClick={() => navigate('/modules')}>Back to Modules</Button>
      </div>
    )
  }

  const isCurrentlyOpen = openModule?._id === moduleId || openModule?.id === moduleId
  const completedLectures = lectures.filter((l) => l.endTime && new Date(l.endTime) < new Date())
  const completionRate = lectures.length > 0 ? ((completedLectures.length / lectures.length) * 100).toFixed(1) : 0

  return (
    <div className="module-detail-page">
      {/* Breadcrumb showing Course → Batch → Module relationship */}
      {course && batch && (
        <div className="module-breadcrumb">
          <Link to="/courses" className="breadcrumb-link">Courses</Link>
          <span className="breadcrumb-separator">→</span>
          <Link to={`/courses/${course._id || course}`} className="breadcrumb-link">
            {course.title || 'Course'}
          </Link>
          <span className="breadcrumb-separator">→</span>
          <Link to={`/batches/${batch._id || batch}`} className="breadcrumb-link">
            {batch.batchName || 'Batch'}
          </Link>
          <span className="breadcrumb-separator">→</span>
          <span className="breadcrumb-current">{module?.title || 'Module'}</span>
        </div>
      )}

      <header className="detail-header">
        <Link to="/modules" className="back-link">
          <FiArrowLeft size={20} />
          Back to Modules
        </Link>
        <div className="header-actions">
          <Button
            variant="secondary"
            onClick={() => handleToggleModule(module?.isOpen)}
          >
            {module?.isOpen ? (
              <>
                <FiLock size={18} />
                Close Module
              </>
            ) : (
              <>
                <FiUnlock size={18} />
                Open Module
              </>
            )}
          </Button>
          <Button variant="secondary" onClick={() => setShowEditModal(true)}>
            <FiEdit size={18} />
            Edit Module
          </Button>
          <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
            <FiTrash2 size={18} />
            Delete
          </Button>
        </div>
      </header>

      <div className="detail-content">
        <div className="detail-main">
          <section className="detail-card">
            <div className="card-header">
              <div>
                <h1>{module?.title}</h1>
                <div className="header-badges">
                  <StatusBadge status={module?.isOpen ? 'active' : 'closed'} />
                  {isCurrentlyOpen && (
                    <span className="currently-open-badge">
                      <FiActivity size={14} />
                      Currently Open
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="info-grid">
              <div className="info-item">
                <FiBook size={18} />
                <div>
                  <span className="info-label">Course</span>
                  <strong>
                    {course ? (
                      <Link to={`/courses/${course._id || course}`} className="info-link">
                        {course.title || 'Unknown Course'}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </strong>
                </div>
              </div>
              <div className="info-item">
                <FiLayers size={18} />
                <div>
                  <span className="info-label">Batch</span>
                  <strong>
                    {batch ? (
                      <Link to={`/batches/${batch._id || batch}`} className="info-link">
                        {batch.batchName || 'Unknown Batch'}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </strong>
                </div>
              </div>
              <div className="info-item">
                <FiActivity size={18} />
                <div>
                  <span className="info-label">Order</span>
                  <strong>{module?.order || '—'}</strong>
                </div>
              </div>
              {module?.duration && (
                <div className="info-item">
                  <FiClock size={18} />
                  <div>
                    <span className="info-label">Duration</span>
                    <strong>{module.duration}</strong>
                  </div>
                </div>
              )}
              <div className="info-item">
                <FiVideo size={18} />
                <div>
                  <span className="info-label">Total Lectures</span>
                  <strong>{lectures.length}</strong>
                </div>
              </div>
              <div className="info-item">
                <FiCheck size={18} />
                <div>
                  <span className="info-label">Completion Rate</span>
                  <strong>{completionRate}%</strong>
                </div>
              </div>
            </div>

            {module?.description && (
              <div className="description-section">
                <h3>Description</h3>
                <p>{module.description}</p>
              </div>
            )}
          </section>

          <section className="detail-card">
            <div className="section-header">
              <h2>Lectures</h2>
              <Button
                size="small"
                onClick={() => {
                  if (batch?._id || batch) {
                    navigate(`/batches/${batch._id || batch}?tab=lectures`)
                  }
                }}
              >
                <FiVideo size={16} />
                Manage Lectures
              </Button>
            </div>
            {lectures.length > 0 ? (
              <Table
                columns={[
                  { key: 'topic', label: 'Topic' },
                  {
                    key: 'teacher',
                    label: 'Teacher',
                    render: (row) =>
                      row.teacher?.user
                        ? `${row.teacher.user.firstName} ${row.teacher.user.lastName}`
                        : '—',
                  },
                  {
                    key: 'startTime',
                    label: 'Start Time',
                    render: (row) =>
                      row.startTime ? new Date(row.startTime).toLocaleString() : '—',
                  },
                  {
                    key: 'endTime',
                    label: 'End Time',
                    render: (row) =>
                      row.endTime ? new Date(row.endTime).toLocaleString() : '—',
                  },
                  {
                    key: 'status',
                    label: 'Status',
                    render: (row) => {
                      const now = new Date()
                      const start = new Date(row.startTime)
                      const end = new Date(row.endTime)
                      if (end < now) return <StatusBadge status="completed" />
                      if (start <= now && end >= now) return <StatusBadge status="ongoing" />
                      return <StatusBadge status="upcoming" />
                    },
                  },
                ]}
                data={lectures}
              />
            ) : (
              <p className="empty-state">No lectures scheduled for this module</p>
            )}
          </section>

          {batchModules.length > 1 && (
            <section className="detail-card">
              <div className="section-header">
                <h2>Module Ordering</h2>
                <p className="info-text">
                  Change the order number to reorder modules. Lower numbers appear first.
                </p>
              </div>
              <div className="reorder-list">
                {batchModules.map((batchModule) => (
                  <div
                    key={batchModule._id}
                    className={`reorder-item ${batchModule._id === moduleId ? 'current-module' : ''}`}
                  >
                    <div className="reorder-handle">
                      <FiPackage size={16} />
                      <span className="reorder-number">{batchModule.order || 1}</span>
                    </div>
                    <span className="reorder-label">{batchModule.title}</span>
                    {batchModule._id === moduleId && (
                      <span className="current-badge">Current</span>
                    )}
                    <div className="reorder-controls">
                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() =>
                          handleReorderModule(
                            batchModule._id,
                            Math.max(1, (batchModule.order || 1) - 1)
                          )
                        }
                        disabled={batchModule.order <= 1}
                        title="Move Up"
                      >
                        ↑
                      </Button>
                      <input
                        type="number"
                        min="1"
                        value={batchModule.order || 1}
                        onChange={(e) => {
                          const newOrder = parseInt(e.target.value) || 1
                          if (newOrder > 0) {
                            handleReorderModule(batchModule._id, newOrder)
                          }
                        }}
                        className="order-input"
                        title="Order number"
                      />
                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() =>
                          handleReorderModule(
                            batchModule._id,
                            (batchModule.order || 1) + 1
                          )
                        }
                        title="Move Down"
                      >
                        ↓
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="detail-card">
            <div className="section-header">
              <h2>Statistics</h2>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <FiVideo size={24} />
                <div>
                  <span className="stat-label">Total Lectures</span>
                  <strong className="stat-value">{lectures.length}</strong>
                </div>
              </div>
              <div className="stat-card">
                <FiCheck size={24} />
                <div>
                  <span className="stat-label">Completed</span>
                  <strong className="stat-value">{completedLectures.length}</strong>
                </div>
              </div>
              <div className="stat-card">
                <FiTrendingUp size={24} />
                <div>
                  <span className="stat-label">Completion Rate</span>
                  <strong className="stat-value">{completionRate}%</strong>
                </div>
              </div>
              <div className="stat-card">
                <FiClock size={24} />
                <div>
                  <span className="stat-label">Duration</span>
                  <strong className="stat-value">{module?.duration || '—'}</strong>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="detail-sidebar">
          <section className="detail-card">
            <h3>Quick Actions</h3>
            <div className="action-list">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => {
                  if (batch?._id || batch) {
                    navigate(`/batches/${batch._id || batch}`)
                  }
                }}
              >
                <FiLayers size={16} />
                View Batch
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => {
                  if (course?._id || course) {
                    navigate(`/courses/${course._id || course}`)
                  }
                }}
              >
                <FiBook size={16} />
                View Course
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => {
                  if (batch?._id || batch) {
                    navigate(`/batches/${batch._id || batch}?tab=lectures`)
                  }
                }}
              >
                <FiVideo size={16} />
                Schedule Lecture
              </Button>
            </div>
          </section>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Module"
        size="medium"
      >
        {batch && (
          <div className="module-context-info">
            <div className="context-breadcrumb">
              <span className="context-item">
                <strong>Course:</strong> {course?.title || batch.course?.title || 'Unknown Course'}
              </span>
              <span className="context-separator">→</span>
              <span className="context-item">
                <strong>Batch:</strong> {batch.batchName}
              </span>
              <span className="context-separator">→</span>
              <span className="context-item active">
                <strong>Module:</strong> {module?.title || 'Edit Module'}
              </span>
            </div>
          </div>
        )}
        <form onSubmit={handleUpdate}>
          <FormInput
            label="Module Title"
            name="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
          <FormInput
            label="Description"
            name="description"
            type="textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <FormInput
            label="Order"
            name="order"
            type="number"
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
            required
          />
          <FormInput
            label="Duration"
            name="duration"
            value={formData.duration}
            onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
            placeholder="e.g., 2 weeks, 1 month"
          />
          <div className="form-checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.isOpen}
                onChange={(e) => setFormData({ ...formData, isOpen: e.target.checked })}
              />
              <span>Module is open for students</span>
            </label>
          </div>
          <div className="modal-actions">
            <Button variant="secondary" type="button" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={editLoading}>
              Update Module
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Module"
        size="small"
      >
        <p>Are you sure you want to delete "{module?.title}"?</p>
        <p className="warning-text">This action cannot be undone.</p>
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} loading={deleteLoading}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default ModuleDetail

